import { BaseWizardStep } from "../base.wizard.step";
import { cloneDeep, find, first, flatten, forEach, isArray, isEmpty, isObject, map } from 'lodash';
import { ApiService } from "@nrwl/common-lib";
import { AbstractControl } from "@angular/forms";

export class SitesWizardStep extends BaseWizardStep {

  addNewItemCallback: any;
  methodTypeSource: any;
  selectedMethod: any;
  selectedDataSource: any;
  dataSourceSelected = false;

  dataStoreType: string;

  constructor(apiService: ApiService, config: any, parent: any) {

    super(apiService, config, parent);

    this.methodTypeSource = [{
      "id": "mannual",
      "iconSrc": "images/site.svg",
      "label": "Add a site manually",
      "description": ""
    }, {
      "id": "uploadCSV",
      "iconSrc": "images/subnets.svg",
      "label": "Upload a CSV file",
      "description": "A comma delimited list of site information. Column headers are required and in this order: site, city, state, country, latitude, longitude, subnetname, subnetdescription, subnetcidr, tags"
    }];

    this.selectedDataSource = {};
    // is bound as input to some wrapped components/used as callback
    this.addNewItemCallback = () => {
      this.addNewItem();
    };
    this.selectedMethod = {
      id: ''
    };
    if (this.config.def.subContent) {
      this.subContent = new SitesWizardStep(apiService, config.def.subContent, this);
    }
  }

  //---------------------------------------- Request/Format Data -------------------------------------------------------

  _requestSingleData(url, key, onSuccess = () => {
    // Empty function
  }) {
    super._requestSingleData(url, key, onSuccess);
  }

  _onSuccessfulRequest(key, dataSource) {
    super._onSuccessfulRequest(key, dataSource);
  }

  dataValueChange(componentId, key, newValue, isValid = true) {
    super.dataValueChange(componentId, key, newValue, isValid);
  }

  metaDataValueChange(componentId, key, newValue, isValid = true) {
    super.metaDataValueChange(componentId, key, newValue, isValid);
  }

  //------------------------------------------- Step Control -----------------------------------------------------------

  _resetStep() {
    super._resetStep();
  }

  isValid() {
    const isValidStandardControls = (isEmpty(this.form) || this.form.valid) &&
      !find(this.config.def.outputs, (key) => {
        return this._getIndex(key, this.config.def.outputOptional) === -1 && isEmpty(this.output[key]);
      });

    let isValidExtendedControls = true;
    forEach(this.stateOfExtendedControls, (value, key) => {
      isValidExtendedControls = isValidExtendedControls && value.isValid;
    });

    return isValidStandardControls && isValidExtendedControls;
  }



  addError(control: AbstractControl, error: string) {
    const err = control.errors; // get control errors
    if (err) {
      err[error] = true;
      control.setErrors(err); // controls got other errors so set them back
    }
  }

  removeError(control: AbstractControl, error: string) {
    const err = control.errors; // get control errors
    if (err) {
      delete err[error]; // delete your own error
      if (!Object.keys(err).length) { // if no errors left
        control.setErrors(null); // set control errors to null making it VALID
      } else {
        control.setErrors(err); // controls got other errors so set them back
      }
    }
  }

  noErrorsForAgentAliasInput(): boolean {
    if (this.form && this.form.controls.agentAlias) {
      return this.form.controls.agentAlias.valid;
    }
    // most likely, the page has no such control
    // for me this looks weird, but that was original condition in validatePCAgentDetailsForTroubleShootingMonitor() method.
    return true;
  }



  //--------------------------------------------- list actions ---------------------------------------------------------

  getListItemsKeys(dataSources, key) {
    return map(flatten(dataSources), key);
  }

  //-------------------------------------------- Put New Data ------------------------------------------------------------


  addNewItem() {
    if (!this.isNewItemDataValid()) {
      return;
    }
    this._addNewItemToCurrentList(true);
    if (this.parent.showServices) {
      this.subContent._resetStep();
      this.selectedTab = 0;
    }
  }

  isNewItemDataValid(step = this.subContent) {
    const hasEmptyInputs = find(step.config.def.inputsRequired, (key) => {
      if (isObject(key)) {
        return find(key, (items, subKey) => {
          return !find(items, (item: string) => {
            return !isEmpty(step.inputs[subKey]) && !isEmpty(step.inputs[subKey][item]);
          });
        });
      } else if (isEmpty(step.inputs[key])) {
        if (!isEmpty(this.sharedOutput[key])) {
          step.inputs[key] = this.sharedOutput[key];
          return false;
        } else {
          return true;
        }
      }
    });

    return !hasEmptyInputs;
  }

  _addNewItemToCurrentList(selectNewItem) {
    const data = isArray(this.dataSource) ? first(this.dataSource) : this.dataSource;
    const newItem = cloneDeep(this.subContent.inputs);

    newItem.tests = map(this.sharedOutput.testTypes, (test) => {
      return {
        id: test.id,
        label: test.label
      };
    });
    data.list.push(newItem);

    if (selectNewItem) {
      this.onItemClick(this.config.def.outputs[0], newItem);
    }
  }

  //------------------------------------------- Click Events -----------------------------------------------------------

  onItemClick(outputKey, item) {
    if (outputKey === 'creationType') {
      this.selectedMethod = item;
      this.itemSelected = true;
      this.parent.showUploadSites = item.id === "uploadCSV";
      return;
    }
    this.itemSelected = true;
    return this._getClickOnItemFunction(this.config.def.stepType).bind(this)(outputKey, item);
  }

  selectTypeContent(type, key, showSubContent = false) {
    this.dataStoreType = key;

    if (type === 'dataSource') {
      this.dataSourceSelected = false;
      this.itemSelected = false;
      this.selectedDataSource = {};
      this.parent.output.source = this.selectedDataSource;
    }
    else if (type === 'uploadSiteDetails') {
      this.parent.output = key;
    }
    this.subContent.toggleSubContent(true);

  }

  // Angular calls this method before actual changes have occurred in this.output (which is bound to ngModel)
  // therefore using incoming value/which represents 'new' value to be bound to ngModel.
  onChangeSelection() {
    this.itemSelected = false;
    this._resetStep();
    if (this.parent.defaultParamsById.isTroubleshooting) {
      this.parent.output.isTroubleshooting = true;
    }
  }

  addSelectedItemToDataSource(item, type) {
    this.itemSelected = true;
    this.selectedDataSource = {
      "id": item.id,
      "type": type,
      "name": item.name
    };
  }

  removeDatasource(item) {
    const index: number = this.parent.output.source.indexOf(item);
    if (index !== -1) {
      this.parent.output.source.splice(index, 1);
    }
  }


  closeSubContentStep() {
    this.subContent.toggleSubContent(false);
  }


  //-------------------------------------------------Helpers------------------------------------------------------------


}
